/* ═══════════════════════════════════════════════════════════════
   ViewMode — "My hours" read-only rolling-weeks view (Mode 2). Zero
   config: no filters, no scope picker, no saved views — just the
   signed-in member's own forecast, one click from the page header.
   Most day-to-day consumption of forecasts happens as timesheet
   ghosts (D7, Tranche 2); this is the quick "what did I plan?" check.
   ═══════════════════════════════════════════════════════════════ */

import React, { useMemo } from 'react';
import type {
  TeamMember,
  Project,
  HourForecast,
  Department,
  Team,
  TeamAssignment,
  ProjectAssignment,
} from '../../../shared/types';
import EmptyState from '../../components/EmptyState';
import { buildPersonPivotRows, bucketByWeek, sumHours } from './buildPlanRows';
import { addDays, formatShort, mondayOf, todayIso, weekWindow } from './utils';
import { rem } from '../../lib/rem';

const LABEL_COL = 220;
const WEEK_COL = 100;

export function ViewMode({
  currentUserId,
  members,
  projects,
  forecasts,
  departments,
  teams,
  teamAssignments,
  projectAssignments,
}: {
  currentUserId: string;
  members: TeamMember[];
  projects: Project[];
  forecasts: HourForecast[];
  departments: Department[];
  teams: Team[];
  teamAssignments: TeamAssignment[];
  projectAssignments: ProjectAssignment[];
}) {
  const me = useMemo(() => members.find((m) => m.id === currentUserId), [members, currentUserId]);
  const weeks = useMemo(() => weekWindow(mondayOf(todayIso()), 8), []);
  const currentWeek = weeks[0];

  const groups = useMemo(() => {
    if (!me) return [];
    return buildPersonPivotRows({
      members: [me],
      projects,
      forecasts: forecasts.filter((f) => f.teamMemberId === currentUserId),
      weeks,
      layout: 'flat',
      departments,
      teams,
      teamAssignments,
      projectAssignments,
      pendingPairings: new Map(),
    });
  }, [
    me,
    projects,
    forecasts,
    weeks,
    departments,
    teams,
    teamAssignments,
    projectAssignments,
    currentUserId,
  ]);

  if (!me) {
    return (
      <div style={{ padding: 'var(--space-5)' }}>
        <EmptyState
          title="No team member record"
          subtitle="Your account isn't linked to a team member, so there's nothing to show."
        />
      </div>
    );
  }

  const pairings = groups[0]?.parents[0]?.pairings || [];
  const myForecasts = forecasts.filter((f) => f.teamMemberId === currentUserId);

  if (pairings.length === 0) {
    return (
      <div style={{ padding: 'var(--space-5)' }}>
        <EmptyState
          title="Nothing forecasted yet"
          subtitle="Hours you plan (or your PM plans for you) show up here as soon as they're entered."
        />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflow: 'auto' }}>
      <div
        style={{
          minWidth: LABEL_COL + weeks.length * WEEK_COL,
          display: 'grid',
          gridTemplateColumns: `${LABEL_COL}px repeat(${weeks.length}, ${WEEK_COL}px)`,
        }}
      >
        <div style={headerCellStyle}>My hours</div>
        {weeks.map((wk) => (
          <div
            key={wk}
            style={{
              ...headerCellStyle,
              textAlign: 'center',
              background:
                wk === currentWeek ? 'color-mix(in srgb, var(--ac) 8%, var(--sf))' : 'var(--sf)',
              color: wk === currentWeek ? 'var(--ac)' : 'var(--tx)',
            }}
          >
            {formatShort(wk)}
            {wk === currentWeek && (
              <div style={{ fontSize: 'calc(var(--font-size-base) * 9 / 13)', fontWeight: 700 }}>
                THIS WEEK
              </div>
            )}
          </div>
        ))}

        {pairings.map((pairing) => {
          const rows = myForecasts.filter((f) =>
            pairing.isVacation
              ? f.sourceKind === 'vacation'
              : f.projectId === pairing.projectId && f.sourceKind !== 'vacation'
          );
          const byWeek = bucketByWeek(rows);
          return (
            <React.Fragment key={pairing.id}>
              <div
                style={{
                  padding: `var(--space-2) ${rem(14)}`,
                  borderBottom: '1px solid var(--bd)',
                  background: pairing.isVacation
                    ? 'color-mix(in srgb, var(--tm) 5%, var(--sf))'
                    : 'var(--sf)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: rem(9),
                    height: rem(9),
                    borderRadius: rem(2),
                    background: pairing.color || 'var(--bd)',
                  }}
                />
                <span
                  style={{
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    fontWeight: 500,
                    color: pairing.isVacation ? 'var(--tm)' : 'var(--tx)',
                  }}
                >
                  {pairing.label}
                </span>
              </div>
              {weeks.map((wk) => {
                const total = sumHours(byWeek.get(wk));
                return (
                  <div
                    key={wk}
                    style={{
                      padding: 'var(--space-2)',
                      borderBottom: '1px solid var(--bd)',
                      borderLeft: '1px solid var(--bd)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                      fontWeight: 600,
                      color: total > 0 ? 'var(--tx)' : 'var(--tm)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {total > 0 ? `${total}h` : '—'}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}

        {/* Week totals footer */}
        <div
          style={{
            padding: `var(--space-2) ${rem(14)}`,
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            color: 'var(--tm)',
            borderTop: '2px solid var(--bd)',
          }}
        >
          Week total
        </div>
        {weeks.map((wk) => {
          const total = myForecasts.reduce((s, f) => {
            const day = f.dayDate || f.weekStartDate;
            if (!day || mondayOf(day) !== wk) return s;
            return s + (Number(f.hours) || 0);
          }, 0);
          return (
            <div
              key={wk}
              style={{
                padding: 'var(--space-2)',
                borderTop: '2px solid var(--bd)',
                borderLeft: '1px solid var(--bd)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {total > 0 ? `${total}h` : '—'}
            </div>
          );
        })}
      </div>
      <div
        style={{
          padding: `var(--space-2) ${rem(14)}`,
          fontSize: 'var(--font-size-sm)',
          color: 'var(--tm)',
        }}
      >
        Range shown: {formatShort(weeks[0])} – {formatShort(addDays(weeks[weeks.length - 1], 6))}.
        Read-only — edit in Plan mode.
      </div>
    </div>
  );
}

const headerCellStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  background: 'var(--sf)',
  borderBottom: '1px solid var(--bd)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};
